import React, { useState } from 'react';
import { FormLayoutComposer, IFormLayoutProps } from 'components/common/form/renderer/FormLayoutComposer';
import { IImportResultData } from 'app/content/import';
import ImportResultPopup from 'app/content/import/ImportResultPopup';
import { formAPI } from 'api/form';
import { useQueryClient } from '@tanstack/react-query';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { createPortal } from 'react-dom';
import { ActSubmit } from 'components/common/form/act';
import pollingService from 'tools/pollingService';

export default function ImportForm(props: IFormLayoutProps) {
    const queryClient = useQueryClient();

    const [showResults, setShowResults] = useState(false);
    const [resultsData, setResultsData] = useState<IImportResultData | null>(null);
    const [loading, setLoading] = useState(false);

    const onSuccess = (data: any) => {
        setShowResults(true);
        setResultsData(data.data);
        setLoading(false);
    };

    const onError = (response: { message: string }) => {
        alert(response.message);
        setLoading(false);
        closeFormPopup();
    };

    const handleSubmit = async (data: any) => {
        setLoading(true);

        return formAPI
            .saveFormData('data/admin/content/import/form', data, props.hookForm.form)
            .then((response) => {
                if (response.data.status === 'QUEUE') {
                    const pplId = response.data?.data?.ppl_id;
                    if (!pplId) {
                        setLoading(false);
                        alert('Unexpected response: missing ppl_id');
                        return;
                    }
                    const { run } = pollingService({
                        onSuccess: onSuccess,
                        onError: onError,
                        requestData: {},
                    });
                    run(pplId);
                } else {
                    setLoading(false);
                }
            })
            .catch((error) => {
                const message = error?.response?.data?.message || error?.message || 'Unknown error';
                alert(message);
                setLoading(false);
                closeFormPopup();
            });
    };

    const closeFormPopup = () => {
        queryClient.invalidateQueries({ queryKey: ['ImportExportGrid'] });
        props.asPopupProps.onHide();
    };

    const onPopupHide = () => {
        setShowResults(false);
        setResultsData(null);
        closeFormPopup();
    };

    const onImportAgain = () => {
        setShowResults(false);
        setResultsData(null);
    };

    props.hookForm.form.formAct.overrideAct(
        'submit',
        class extends ActSubmit {
            async exec(data: any): Promise<any> {
                return handleSubmit(data);
            }
        },
    );

    return (
        <>
            <FormLayoutComposer props={props} />
            {showResults && (
                <ImportResultPopup
                    importResultData={resultsData}
                    onHide={onPopupHide}
                    popupOpen={showResults}
                    onImportAgain={onImportAgain}
                />
            )}
            {loading && createPortal(<LoadingPlaceholder relativeToParent sx={{ zIndex: 9999 }} />, document.body)}
        </>
    );
}
